import React, { useEffect, useContext, useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { VsCodeAuthContext } from '../contextApis/vsCodeAuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, ArrowRight, Check, X } from 'lucide-react';
import { useGoogleLogin } from '@react-oauth/google';
import { local } from '../utils/constants';
import { AuthInput, OAuthRow, Divider, BrandPanel } from './vsCodeSignIn';
import '../styles/sixthDesign.css';

const VsCodeSignUp = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    error,
    loading,
    handleEmailAuth,
    handleAuth,
    setError,
    isAuthenticated,
  } = useContext(VsCodeAuthContext);

  // === All auth logic preserved exactly ===

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const hasOAuthCode = urlParams.get('code');
    const referrer = urlParams.get('referrer');
    const callbackUrl = urlParams.get('callback_url') || urlParams.get('callback');

    if (hasOAuthCode) return;

    if ((referrer === 'vscode' || referrer === 'teams' || callbackUrl) && isAuthenticated()) {
      localStorage.removeItem('is_authenticated');
      localStorage.removeItem('auth_data');
      localStorage.removeItem('jwt_token');
      localStorage.removeItem('web_access_token');
      localStorage.removeItem('vscode_jwt_token');
      localStorage.removeItem('api_key');
      localStorage.removeItem('user_email');
      localStorage.removeItem('email');
      return;
    }

    if (isAuthenticated()) {
      navigate('/vscode/dashboard');
    }
  }, [isAuthenticated, navigate, location.search]);

  const googleLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      handleAuth('google', tokenResponse.code, true);
    },
    onError: (err) => setError(`Google login failed ${err}`),
    flow: 'auth-code',
    redirect_uri: local ? 'http://localhost:3000/vscode/signup' : 'https://app.trysixth.com/vscode/signup',
  });

  const handleGitHubLogin = () => {
    const clientId = local ? 'Ov23liJf8RKWHaWd7FM9' : 'Ov23liRQBlkU8uk6vpOG';
    const urlParams = new URLSearchParams(window.location.search);
    const referrer = urlParams.get('referrer');
    const callbackUrl = urlParams.get('callback_url') || urlParams.get('callback');
    const teamId = urlParams.get('team_id');

    let redirectUri = local ? 'http://localhost:3000/vscode/signup' : 'https://app.trysixth.com/vscode/signup';
    const params = [];
    if (referrer) params.push(`referrer=${referrer}`);
    if (callbackUrl) params.push(`callback_url=${encodeURIComponent(callbackUrl)}`);
    if (teamId) params.push(`team_id=${teamId}`);
    if (params.length > 0) {
      redirectUri += '?' + params.join('&');
    }

    const scope = 'user:email';
    const githubAuthUrl = `https://github.com/login/oauth/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${scope}`;
    window.location.href = githubAuthUrl;
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const emailParam = searchParams.get('email');
    const codeParam = searchParams.get('code');
    const referrerParam = searchParams.get('referrer');
    const callbackUrlParam = searchParams.get('callback_url') || searchParams.get('callback');
    const teamIdParam = searchParams.get('team_id');

    if (referrerParam === 'vscode' || referrerParam === 'cli') {
      localStorage.setItem('vscode_referrer', referrerParam);
    } else if (referrerParam === 'teams') {
      localStorage.setItem('vscode_referrer', 'teams');
      if (teamIdParam) localStorage.setItem('pending_team_join_id', teamIdParam);
    } else if (referrerParam === null && !codeParam) {
      localStorage.removeItem('vscode_referrer');
      localStorage.removeItem('vscode_callback_url');
      localStorage.removeItem('pending_team_join_id');
    }

    if (callbackUrlParam && (callbackUrlParam.startsWith('vscode://') || callbackUrlParam.startsWith('http'))) {
      localStorage.setItem('vscode_callback_url', callbackUrlParam);
    }

    if (emailParam) setEmail(emailParam);
    if (codeParam) handleAuth('github', codeParam, true);
  }, [location, handleAuth]);

  const passwordRequirements = [
    { id: 'length', met: password.length >= 8, text: '8+ characters' },
    { id: 'uppercase', met: /[A-Z]/.test(password), text: 'Uppercase' },
    { id: 'lowercase', met: /[a-z]/.test(password), text: 'Lowercase' },
    { id: 'number', met: /\d/.test(password), text: 'Number' },
    { id: 'special', met: /[^A-Za-z0-9]/.test(password), text: 'Symbol' },
  ];

  const pwScore = passwordRequirements.filter((r) => r.met).length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) return;

    const urlParams = new URLSearchParams(location.search);
    const teamIdFromUrl = urlParams.get('team_id');
    const teamIdFromStorage = localStorage.getItem('pending_team_join_id');
    const teamId = teamIdFromUrl || teamIdFromStorage;

    const generateUserId = () =>
      Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');

    const payload = {
      email,
      password,
      auth_provider: 'email',
      ...(teamId && {
        team_id: teamId,
        id: generateUserId(),
        user_type: 'developer',
        fullname: email.split('@')[0],
      }),
    };
    handleEmailAuth(payload, true);
  };

  const isFormValid =
    email && password && confirmPassword && password === confirmPassword && pwScore >= 3;

  // === UI ===

  return (
    <div className="sixth-design sd-page-enter" style={{ minHeight: '100vh' }}>
      <div className="sd-auth-shell" style={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: '1.05fr 1fr',
      }}>
        <BrandPanel kind="signup" />

        <div style={{
          padding: '60px 44px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ width: '100%', maxWidth: 400 }}>
            <div style={{ marginBottom: 26 }}>
              <h1 style={{ fontSize: 32, fontWeight: 500, letterSpacing: '-0.025em', margin: '0 0 8px', color: 'var(--text)' }}>
                Create your account
              </h1>
              <p style={{ fontSize: 14.5, color: 'var(--text-2)', margin: 0 }}>
                Already have one?{' '}
                <Link
                  to={`/vscode/signin${location.search}`}
                  style={{
                    color: 'var(--accent-2)', textDecoration: 'underline', textUnderlineOffset: 3,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Sign in →
                </Link>
              </p>
            </div>

            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  style={{
                    marginBottom: 16,
                    padding: '10px 14px',
                    borderRadius: 10,
                    background: 'rgba(255,107,107,0.06)',
                    border: '1px solid rgba(255,107,107,0.2)',
                    color: 'var(--danger)',
                    fontSize: 13,
                    textAlign: 'center',
                  }}
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            <OAuthRow onGoogle={() => googleLogin()} onGithub={handleGitHubLogin} disabled={loading} />
            <div style={{ margin: '22px 0' }}>
              <Divider text="or with email" />
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <AuthInput
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoFocus
              />
              <AuthInput
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                suffix={
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    style={{
                      background: 'none', border: 0, color: 'var(--text-3)',
                      cursor: 'pointer', padding: 6, display: 'inline-flex',
                    }}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                }
              />

              {password.length > 0 && (
                <div style={{ marginTop: -6 }}>
                  <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                    {[0, 1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        style={{
                          flex: 1, height: 3, borderRadius: 999,
                          background: i < pwScore
                            ? (pwScore < 3 ? 'var(--warn)' : pwScore < 5 ? 'var(--accent)' : 'var(--success)')
                            : 'rgba(255,255,255,0.06)',
                          transition: 'background 0.15s',
                        }}
                      />
                    ))}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', fontSize: 11.5 }}>
                    {passwordRequirements.map((r) => (
                      <span
                        key={r.id}
                        style={{
                          color: r.met ? 'var(--success)' : 'var(--text-4)',
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                        }}
                      >
                        {r.met ? <Check size={10} strokeWidth={2.5} /> : <X size={10} strokeWidth={2.5} />}
                        {r.text}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <AuthInput
                label="Confirm password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter password"
                error={confirmPassword && password !== confirmPassword ? "Passwords don't match" : null}
                suffix={
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((s) => !s)}
                    style={{
                      background: 'none', border: 0, color: 'var(--text-3)',
                      cursor: 'pointer', padding: 6, display: 'inline-flex',
                    }}
                  >
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                }
              />

              <button
                type="submit"
                className="sd-btn sd-btn-accent"
                disabled={loading || !isFormValid}
                style={{ height: 46, width: '100%', fontSize: 14.5, marginTop: 4 }}
              >
                {loading ? <span className="sd-spinner" /> : <>Create account <ArrowRight size={14} /></>}
              </button>
            </form>

            <p style={{
              fontSize: 12, color: 'var(--text-4)', textAlign: 'center',
              marginTop: 22, lineHeight: 1.5,
            }}>
              By continuing you agree to our{' '}
              <Link to="/terms" style={{ color: 'var(--text-3)', textDecoration: 'underline' }}>Terms</Link>{' '}
              and{' '}
              <Link to="/privacy-policy" style={{ color: 'var(--text-3)', textDecoration: 'underline' }}>Privacy Policy</Link>.<br />
              We'll send a verification code to confirm your email.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VsCodeSignUp;
